import { MATS } from "../materials.js";
import * as H from "./spec.js";

/**
 * The ground storey of the hero house: the rooms you walk into.
 *
 * The shell already encloses the volume, so nothing here invents a dimension,
 * and since the client's floor plan arrived it does not invent a room line
 * either -- every one of them is a field of H.PLAN.  The floor is
 * H.INSIDE.floor (0.55, the level of the front doorstep and the porch slab),
 * the ceiling is the first-floor line at 3.35, and one wall does the work:
 *
 *      z -11.20 +-----------------+----------------------+
 *               |                 |[  open  ]            |
 *               |  LIVING         |         KITCHEN      |
 *               |  back window    |         side window, |
 *               |                 |         PATIO DOOR ->|  deck
 *      z  -6.20 +- - - - -   - - -|                      |
 *               |                 +----------------------+  kitZ -4.60
 *               |  DINING         |                      |
 *               |  front window   |         GARAGE       |
 *               |  the stair ---->|[door]                |
 *      z   1.20 +----[ DOOR ]-----+----------------------+
 *              x -5.45         1.75                    5.45
 *
 * There is no hall.  The front door opens into the dining end, the stair
 * climbs the east face of the long wall beside you, and the way to the back
 * of the house is straight on -- which is what the plan draws and what makes
 * the ground floor one room deep rather than three rooms and a corridor.
 *
 * The living/dining line is two stubs with the middle open (H.PLAN.livStubs):
 * one room that narrows, not two with a door between them.  The long wall
 * has one real gap in it, H.PLAN.open, which puts the kitchen and the living
 * end into a single space across the back.
 *
 * The three ways in and out, all of them flat:
 *
 *   porch 0.55 -> doorstep 0.59 -> DINING 0.55   (front door, swung open by
 *                                                 openings.js)
 *   KITCHEN 0.55 -> threshold -> DECK 0.55       (patio slider)
 *   DINING 0.55 -> GARAGE 0.55                   (the door in the long wall)
 *
 * Nothing steps at the back door and nothing needs to: the garden is a
 * terrace 0.80 m below the street (spec.GRADE), so the deck stands the 1.35 m
 * above its lawn that it was photographed at and still meets the kitchen
 * floor.  The house is one level throughout -- until the stair, which
 * stairs.js builds and upstairs.js arrives on.
 *
 * Collision: the floor is one box to grade, and each solid wall run is one
 * box.  Nothing else is solid -- skirting, linings and reveals are trim, and
 * a cat catching on invisible trim is the bug this level keeps not having.
 */

const I = H.INSIDE;
const F = I.floor;                       // 0.55
const C = I.ceiling;                     // 3.35
const T = I.wallT;                       // 0.12
const X0 = I.minX, X1 = I.maxX;          // -5.45 .. 5.45
const Z0 = I.minZ, Z1 = I.maxZ;          // -11.20 .. 1.20

const PL = MATS.plaster;
const PC = MATS.ceiling;                 // ceilings and downward soffits
const FB = MATS.floorBoard;
const FS = MATS.floorShade;
const TR = MATS[H.LOOK.trim];
const GD = MATS[H.LOOK.garageDoor];
const CN = MATS.concrete;
const GL = MATS.glassLit;

const LINE = 0.03;                       // plaster skim over the brick
const TOP = C + 0.03;                    // walls die 3 cm INTO the ceiling
// slab rather than stopping flush with it: coplanar faces shimmer, and this
// junction runs right round every room.
const DOOR_H = 2.05;                     // internal doorway, above the floor
const DW = 1.05;                         // internal doorway width
// Indoors, `tint` is 0 rather than merely small.  Walls are emitted as one box
// per run between the holes in them, so per-piece variation does not read as
// variation at all -- it reads as bands drawn across the plaster.  What little
// AO is left darkens each piece's own base, which is what keeps the skirting
// and the flight from floating.
const FLAT = { solid: false, ao: 0.04, tint: 0 };

// The plan, every line of it read from the spec so the two storeys and the
// stair cannot drift from each other or from the drawing.
const DIV = H.PLAN.divX;                 // the long wall, and the stair's back
const LIV = H.PLAN.livZ;                 // living (back) / dining (front)
const KIT = H.PLAN.kitZ;                 // kitchen (back) / garage (front)

/**
 * The internal walls: which plane each stands in, how far it runs, and the
 * doorways left in it.  partitions() builds from this and skirting() reads
 * the same runs, so a wall that moves takes its skirting with it.
 *
 * Three lines and no more, which is what makes this floor read as a plan
 * rather than a warren: the long wall, the kitchen/garage cross-wall behind
 * it, and the living/dining line -- which is not really a wall at all.
 */
const PARTITIONS = [
  // The long wall, in two runs with H.PLAN.open between them.  A gap and a
  // doorway are not the same thing and must not be built the same way: a
  // doorway is a hole with a header over it, and this is a room opening on
  // to another room, so it is the absence of a wall rather than a hole in
  // one.  Splitting the run is how you say that.
  { axis: "z", at: DIV, u0: Z0, u1: H.PLAN.open.z0, doors: [] },
  { axis: "z", at: DIV, u0: H.PLAN.open.z1, u1: Z1,
    doors: [[H.PLAN.garageDoor.z, H.PLAN.garageDoor.w]] },
  // Kitchen from garage.  No doorway: the garage is reached from the dining
  // end through the long wall, which is where the plan puts its door.
  { axis: "x", at: KIT, u0: DIV, u1: X1, doors: [] },
  // Living from dining: two stubs and 2.00 m of nothing between them.
  ...H.PLAN.livStubs.map(([u0, u1]) => ({ axis: "x", at: LIV, u0, u1, doors: [] })),
];

const ROOMS = [
  { name: "dining",  x0: X0,  x1: DIV, z0: LIV, z1: Z1 },
  { name: "living",  x0: X0,  x1: DIV, z0: Z0,  z1: LIV },
  { name: "kitchen", x0: DIV, x1: X1,  z0: Z0,  z1: KIT },
  { name: "garage",  x0: DIV, x1: X1,  z0: KIT, z1: Z1 },
];
const room = (name) => ROOMS.find((r) => r.name === name);

// ---------------------------------------------------------------------------
// Wall-local placement.  `u` runs along the wall, `t` through it; "x" walls
// face along z, "z" walls face along x.  Every helper below is written once
// in these terms and works on all four sides of every room.
function wallBox(b, axis, uc, y, tc, uw, h, td, mat, opts = FLAT) {
  if (axis === "x") b.block(uc, y, tc, uw, h, td, mat, opts);
  else b.block(tc, y, uc, td, h, uw, mat, opts);
}

function wallCollider(b, axis, uc, y, tc, uw, h, td) {
  if (axis === "x") b.collider(uc, y, tc, uw, h, td);
  else b.collider(tc, y, uc, td, h, uw);
}

/** `[a0,a1]` minus every `[g0,g1]`, as the runs that survive. */
function runs(a0, a1, gaps) {
  let out = [[a0, a1]];
  for (const [g0, g1] of gaps) {
    out = out.flatMap(([s0, s1]) => {
      if (g1 <= s0 || g0 >= s1) return [[s0, s1]];
      const keep = [];
      if (g0 > s0) keep.push([s0, g0]);
      if (g1 < s1) keep.push([g1, s1]);
      return keep;
    });
  }
  return out.filter(([s0, s1]) => s1 - s0 > 1e-3);
}

/**
 * A thin panel on a wall plane with rectangles cut out of it.
 *
 * The shell's brickwork trick, one layer in: split the plane at every hole
 * edge, merge the closed cells across each band, and emit one box per run.
 * The plaster then meets every reveal exactly, with nothing to trim by hand.
 */
function panel(b, { axis, at, into, u0, u1, y0, y1, holes = [], mat, t = LINE }) {
  const cut = holes.map((r) => ({
    u0: Math.max(r.u0, u0), u1: Math.min(r.u1, u1),
    y0: Math.max(r.y0, y0), y1: Math.min(r.y1, y1),
  })).filter((r) => r.u1 > r.u0 && r.y1 > r.y0);
  const edges = (vals, lo, hi) => {
    const out = [];
    for (const v of [...vals].sort((p, q) => p - q)) {
      if (v < lo - 1e-4 || v > hi + 1e-4) continue;
      if (!out.length || v - out[out.length - 1] > 1e-4) out.push(v);
    }
    return out;
  };
  const us = edges([u0, u1, ...cut.flatMap((r) => [r.u0, r.u1])], u0, u1);
  const ys = edges([y0, y1, ...cut.flatMap((r) => [r.y0, r.y1])], y0, y1);
  const open = (uc, yc) =>
    cut.some((r) => uc > r.u0 + 1e-4 && uc < r.u1 - 1e-4 &&
                    yc > r.y0 + 1e-4 && yc < r.y1 - 1e-4);
  const tc = at + into * t / 2;
  for (let j = 0; j < ys.length - 1; j++) {
    const ya = ys[j], yb = ys[j + 1], yc = (ya + yb) / 2;
    let run = null;
    for (let i = 0; i < us.length; i++) {
      const closed = i < us.length - 1 && !open((us[i] + us[i + 1]) / 2, yc);
      if (closed && run === null) run = us[i];
      if (!closed && run !== null) {
        // Two things keep the bands from drawing themselves on the wall.
        // ao 0, not merely small: a run spans only its own band, so the
        // base-darkening would restart at every window head and sill.  And
        // 5 mm of overlap into the band above, because bands that butt share
        // a horizontal plane and that seam shimmers along its whole length.
        wallBox(b, axis, (run + us[i]) / 2, ya, tc, us[i] - run,
                yb - ya + 0.005, t, mat, { solid: false, ao: 0, tint: 0 });
        run = null;
      }
    }
  }
}

/**
 * The sides of a hole in an exterior wall, seen from the room.
 *
 * `depth` is how far back the reveal runs before it meets whatever fills the
 * opening.  A window also gets an inner sill: 0.20 m of board, and the one
 * piece here that is solid, because a sill at this height is the perch the
 * whole room is worth climbing for.
 */
function reveal(b, { axis, at, into, u0, u1, y0, y1, depth, sill = false }) {
  const uc = (u0 + u1) / 2, uw = u1 - u0;
  const tc = at + into * depth / 2;
  // The head board laps the plaster rather than butting it.  panel() lays
  // the band above a window as a 0.03 skim on the room face of the brick,
  // and that band has an underside: 3 cm of PLASTER pointing straight down
  // across the full width of every opening, with only the lawn-green half of
  // the hemisphere reaching it.  A head board butted to it sits 3 cm behind
  // that strip and cannot hide it, so the board runs 1 cm proud of the skim
  // and 1 cm up into it -- which buries the face and leaves a shadow line at
  // the head instead.  lining() has always done this for door heads ("2 cm
  // up into the header"); windows never got the same move.  Found upstairs,
  // where the sills are low enough to look up from, and true here all along.
  wallBox(b, axis, uc, y1 - LINE, at + into * (depth - LINE - 0.01) / 2,
          uw, LINE + 0.01, depth + LINE + 0.01, PC);      // head
  for (const s of [-1, 1])
    wallBox(b, axis, uc + s * (uw - LINE) / 2, y0, tc, LINE, y1 - y0, depth, PL);
  if (!sill) return;
  // Daylight pane at the back of the reveal.  The glazing itself is opaque
  // dark glass -- right from the street, a black rectangle from a room -- so
  // the inside gets its own bright face just in front of it.
  wallBox(b, axis, uc, y0 + 0.05, at + into * (depth - 0.02), uw - 0.10,
          y1 - y0 - 0.10, 0.02, GL, { solid: false, ao: 0, tint: 0 });
  const sd = depth + 0.13;                       // projects into the room
  wallBox(b, axis, uc, y0 - 0.06, at + into * depth / 2 - into * 0.065,
          uw + 0.12, 0.06, sd, TR);
  wallCollider(b, axis, uc, y0 - 0.06, at + into * depth / 2 - into * 0.065,
               uw + 0.12, 0.06, sd);
}

/**
 * A cased opening: boards lining the reveal, standing 2 cm proud of the
 * plaster on both faces.
 *
 * They sit INSIDE the opening, overlapping its edge by 5 mm.  Set outside it
 * instead and the frame vanishes: the reveal you actually look at is then the
 * wall's own cut face, and all the lining does is put a 5 cm strip of a very
 * slightly different white on a wall of white.
 *
 * The head board is the ceiling material, not trim.  It is a downward face,
 * and the hemisphere fill paints those lawn-green.
 */
function lining(b, axis, at, uc, uw, thick, y0 = F, h = DOOR_H) {
  const a = uw / 2, d = thick + 0.04;
  for (const s of [-1, 1])
    wallBox(b, axis, uc + s * (a - 0.02), y0, at, 0.05, h + 0.02, d, TR);
  // 2 cm up into the header and 1.5 cm into each jamb, so no two faces meet.
  wallBox(b, axis, uc, y0 + h - 0.05, at, uw - 0.06, 0.07, d, PC,
          { solid: false, ao: 0, tint: 0 });
}

// ---------------------------------------------------------------------------
/** Boards over the living half, a poured slab in the garage, one collider. */
function floor(b) {
  b.block(0, F - 0.09, (Z0 + Z1) / 2, X1 - X0, 0.07, Z1 - Z0,
          MATS.timber, { solid: false, ao: 0 });          // subfloor, dark
  const g = room("garage");
  const pitch = 0.19;
  const n = Math.round((Z1 - Z0) / pitch);
  for (let i = 0; i < n; i++) {
    const zc = Z0 + (i + 0.5) * ((Z1 - Z0) / n);
    // Boards run wall to wall, except where the garage takes the +X end.
    const xEnd = zc > KIT ? g.x0 : X1;
    b.block((X0 + xEnd) / 2, F - 0.03, zc, xEnd - X0, 0.03, pitch - 0.008,
            i % 3 === 2 ? FS : FB, { solid: false, ao: 0, tile: 0.9 });
  }
  b.slab((g.x0 + g.x1) / 2, F - 0.03, (g.z0 + g.z1) / 2, g.x1 - g.x0,
         g.z1 - g.z0, CN, { thickness: 0.03, tile: 1.6 });

  // One box to grade: the floor is solid, and nothing lives under it.
  b.collider(0, 0, (Z0 + Z1) / 2, X1 - X0, F, Z1 - Z0);
}

/**
 * Flat ceiling at the first-floor line -- and the underside of the storey
 * above, which is why it has a hole in it.
 *
 * H.STAIRWELL is the rectangle the stair climbs through.  It is cut here and
 * again out of the boards upstairs.js lays on top; both read the same export,
 * so a ceiling with a hole and a floor with a hole cannot end up disagreeing
 * about where the hole is.  Four bands rather than a general cutter: the hole
 * is one rectangle in one plane, and four explicit rectangles are the whole
 * of the arithmetic.
 */
function ceiling(b) {
  // Oversized by 4 cm so its edges finish inside the brick, not on it.
  const x0 = X0 - 0.04, x1 = X1 + 0.04;
  const z0 = Z0 - 0.04, z1 = Z1 + 0.04;
  const W = H.STAIRWELL;
  const band = (a0, a1, b0, b1) =>
    b.block((a0 + a1) / 2, C, (b0 + b1) / 2, a1 - a0, 0.09, b1 - b0, PC,
            { solid: false, ao: 0, tint: 0 });
  band(x0, x1, W.z1, z1);              // in front of the well
  band(x0, x1, z0, W.z0);              // behind it
  band(x0, W.x0, W.z0, W.z1);          // west of it, past the stair head
  band(W.x1, x1, W.z0, W.z1);          // east of it, over the landing
}

/**
 * Which of the shell's holes are this storey's business.
 *
 * Every window in the house comes back `through` now that there is a room
 * behind all of them, upstairs included -- so `through` alone no longer says
 * "mine".  panel() clips its holes to the wall it is drawing and quietly
 * drops the ones above the ceiling, but reveal() does not: unfiltered, this
 * file hung six sills and six daylight panes at 4.05 m, which is inside the
 * ground floor's ceiling and inside upstairs.js's floor.  upstairs.js makes
 * the complementary test.
 */
const ours = (w) => w.y1 <= C;

/** Plaster over the brick, cut round every window and door. */
function walls(b) {
  const fd = H.opening("frontDoor");
  const lr = H.opening("livingRoom");
  const BD = H.BACK_DOOR;
  const rect = (o) => ({ u0: o.x - o.w / 2, u1: o.x + o.w / 2,
                         y0: o.y - o.h / 2, y1: o.y + o.h / 2 });
  const back = H.WALL_T - 0.095;         // reveal depth to the window frames
  const doorBack = H.WALL_T - LINE;      // doorways are lined right through

  // --- front wall ---
  const fdR = { ...rect(fd), y0: F };
  const lrR = rect(lr);
  panel(b, { axis: "x", at: Z1, into: -1, u0: X0, u1: X1, y0: F, y1: TOP,
             holes: [fdR, lrR], mat: PL });
  reveal(b, { axis: "x", at: Z1, into: 1, ...lrR, depth: back, sill: true });
  reveal(b, { axis: "x", at: Z1, into: 1, ...fdR, depth: doorBack });
  lining(b, "x", Z1 - LINE / 2, fd.x, fd.w, LINE, F, fdR.y1 - F);

  // The garage door read from inside: the shell backs that opening with
  // brick, so this is a panel on the wall rather than a hole in it.
  const gd = H.opening("garageDoor");
  b.block(gd.x, F, Z1 - LINE - 0.02, gd.w - 0.04, 2.32 - F, 0.04, GD,
          { solid: false, ao: 0.03 });
  for (let k = 1; k <= 3; k++)
    b.block(gd.x, F + k * (2.32 - F) / 4, Z1 - LINE - 0.055, gd.w - 0.10,
            0.05, 0.035, GD, { solid: false, ao: 0 });

  // --- back wall ---
  const bdR = { u0: BD.x - BD.w / 2, u1: BD.x + BD.w / 2,
                y0: BD.sill, y1: BD.head };
  const bw = H.sideWindows("back").filter(ours);
  panel(b, { axis: "x", at: Z0, into: 1, u0: X0, u1: X1, y0: F, y1: TOP,
             holes: [...bw, bdR], mat: PL });
  for (const w of bw)
    reveal(b, { axis: "x", at: Z0, into: -1, ...w, depth: back, sill: true });
  reveal(b, { axis: "x", at: Z0, into: -1, ...bdR, depth: doorBack });
  lining(b, "x", Z0 + LINE / 2, BD.x, BD.w, LINE, BD.sill, BD.head - BD.sill);

  // --- the two side walls ---
  for (const [wall, at, into] of [["left", X0, 1], ["right", X1, -1]]) {
    const wins = H.sideWindows(wall).filter(ours);
    panel(b, { axis: "z", at, into, u0: Z0, u1: Z1, y0: F, y1: TOP,
               holes: wins, mat: PL });
    for (const w of wins)
      reveal(b, { axis: "z", at, into: -into, ...w, depth: back, sill: true });
  }
}

/**
 * The three internal walls.
 *
 * Each is one plastered stud with its doorways left open and lined; the solid
 * runs carry the colliders, the headers over the doorways do not (they start
 * at 2.60, and a cat that jumps 0.95 off the floor tops out at 1.50).
 */
function partitions(b) {
  for (const { axis, at, u0, u1, doors } of PARTITIONS) {
    const gaps = doors.map(([c, w]) => [c - w / 2, c + w / 2]);
    for (const [a0, a1] of runs(u0, u1, gaps)) {
      wallBox(b, axis, (a0 + a1) / 2, F, at, a1 - a0, TOP - F, T, PL);
      wallCollider(b, axis, (a0 + a1) / 2, F, at, a1 - a0, C - F, T);
    }
    for (const [c, w] of doors) {
      wallBox(b, axis, c, F + DOOR_H, at, w, TOP - F - DOOR_H, T, PL);
      lining(b, axis, at, c, w, T);
    }
  }
}

const near = (a, c) => Math.abs(a - c) < 0.02;
const isOuter = (at) => [Z0, Z1, X0, X1].some((v) => near(at, v));

/**
 * The stretches of a wall plane that actually have a wall in them.
 *
 * Skirting has to follow this rather than the outline of the room, because
 * the rooms are no longer all boxed in: run a board along a room's edge and
 * where the plan leaves that edge open you get a skirting board standing in
 * mid-air across the opening.
 */
function wallRunsOn(axis, at, a0, a1) {
  const door = (c, w) => [c - w / 2 - 0.09, c + w / 2 + 0.09];
  if (isOuter(at)) {                              // brick: solid but for doors
    const gaps = [];
    if (axis === "x" && near(at, Z1)) {
      const fd = H.opening("frontDoor"), gd = H.opening("garageDoor");
      gaps.push(door(fd.x, fd.w), [gd.x - gd.w / 2, gd.x + gd.w / 2]);
    }
    if (axis === "x" && near(at, Z0))
      gaps.push(door(H.BACK_DOOR.x, H.BACK_DOOR.w));
    return runs(a0, a1, gaps);
  }
  const out = [];
  for (const p of PARTITIONS) {
    if (p.axis !== axis || !near(p.at, at)) continue;
    out.push(...runs(Math.max(p.u0, a0), Math.min(p.u1, a1),
                     p.doors.map(([c, w]) => door(c, w))));
  }
  return out;
}

/** Skirting along every wall a room actually has, stopped at each doorway. */
function skirting(b) {
  const h = 0.12, t = 0.025;
  for (const r of ROOMS) {
    if (r.name === "garage") continue;            // a garage has no skirting
    for (const [axis, at, into, a0, a1] of [
      ["x", r.z1, -1, r.x0, r.x1], ["x", r.z0, 1, r.x0, r.x1],
      ["z", r.x0, 1, r.z0, r.z1], ["z", r.x1, -1, r.z0, r.z1],
    ]) {
      const inset = isOuter(at) ? LINE : T / 2;   // plaster skim vs partition
      for (const [s0, s1] of wallRunsOn(axis, at, a0, a1)) {
        if (s1 - s0 < 0.12) continue;
        wallBox(b, axis, (s0 + s1) / 2, F,
                at + into * (inset + t / 2), s1 - s0, h, t, TR);
      }
    }
  }
}

// ---------------------------------------------------------------------------
export function build(b) {
  floor(b);
  walls(b);
  partitions(b);
  skirting(b);
  ceiling(b);

  // A room's centre is a fair guess at "somewhere in this room" for three of
  // the four.  The dining room's centre is now the middle of the staircase,
  // so it is marked in the clear floor west of the flight instead -- the
  // spot the route through the house actually goes through.
  for (const r of ROOMS)
    b.mark(`inside.${r.name}`,
           r.name === "dining" ? (X0 + H.STAIR.low.nose) / 2 : (r.x0 + r.x1) / 2,
           F, r.name === "dining" ? -2.40 : (r.z0 + r.z1) / 2);

  // The two thresholds, as the route in and the route out.
  b.mark("inside.frontDoor", H.X.door, F, Z1 - 0.4);
  b.mark("inside.patioDoor", H.BACK_DOOR.x - 0.45, F, Z0 + 0.3);
}
